<template>
    <b-modal
        id="modal-student-upload-assignment"
        ref="modal-student-upload-assignment"
        centered
        ok-title="Subir"
        ok-variant="primary"
        cancel-title="Cancelar"
        title="Realizar Entrega"
        :ok-disabled="disableOkButton"
        @ok="onUpload"
        @show="loadCurrentStudentAssignmentFile()"
        @hidden="resetForm()"
    >
        <!-- Assignment Information -->
        <div v-if="selectedAssignment">
            <h4>{{ selectedAssignment.name }}</h4>
            <h5>
                Fecha límite:
                <strong :class="selectedAssignment.class">
                    {{ selectedAssignment.displayDeadlineDate }}
                </strong>
            </h5>
            <h5>
                Tamaño máximo: <strong>{{ this.maxSizeInMB }}</strong> MB
            </h5>
        </div>
        <hr />
         <div
            v-if="!dataLoaded"
            class="text-center"
        >
            <b-spinner
                label="Cargando..."
                variant="primary"
            />
            <h5>Cargando entrega...</h5>
        </div>
        <div v-else-if="currentFile">
            <h4>Última actualización</h4>
            <h5>Nombre original: {{ currentFile.originalFilename }}</h5>
            <h5>Fecha de subida: {{ currentFile.latestUpdateDate }}</h5>
            <div class="text-center py-2">
                <b-button variant="primary" :href="downloadCurrentFileURL">
                    <b-icon-download /> Descargar ({{ displayCurrentFileSize }})
                </b-button>
            </div>
        </div>
        <h5 v-else>
            No ha subido archivos para esta entrega.
        </h5>
        <hr />
        <b-form id="assignment-upload-form" @submit.stop.prevent="onSubmit()">
            <b-form-group
                id="input-group-student-assignment-file"
                label="*Archivo:"
                label-for="input-student-assignment-file"
                label-size="lg"
                description="Solo se permite un (1) archivo, en caso de entregar más archivos, por favor comprímalos en formato .zip o .rar"
            >
                <b-form-file
                    id="input-student-assignment-file"
                    v-model="$v.form.file.$model"
                    size="lg"
                    placeholder="Escoja o arrastre un archivo aquí..."
                    drop-placeholder="Arrastre archivo aquí..."
                    browse-text="Buscar"
                    :state="validateState('file')"
                    :disabled="formDisabled"
                    :file-name-formatter="formatNames"
                />

                <b-form-invalid-feedback id="input-student-assignment-file-feedback">
                    <ul>
                        <li v-if="!$v.form.file.required">Un archivo es requerido.</li>
                        <li v-if="!$v.form.file.correctFormat">El tamaño del archivo debe ser menor a {{ maxSizeInMB }} Megabytes.</li>
                        <li v-if="!$v.form.file.justOneFile">Solo se permite subir un único archivo.</li>
                    </ul>
                </b-form-invalid-feedback>
            </b-form-group>
        </b-form>
    </b-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { mapState, mapActions } from 'vuex'

export default {
    name: 'StudentAssignmentUploadModal',
    data () {
        return {
            dataLoaded: false,
            formDisabled: false,
            form: {
                fileId: null,
                file: null
            },
            currentFile: null
        }
    },
    validations: {
        form: {
            file: {
                required,
                correctSize: function (val) {
                    if (val === null) return true
                    var file = val
                    return file.size <= this.selectedAssignment.maxSize
                },
                justOneFile: function (val) {
                    if (val === null) return true
                    return val instanceof File
                }
            }
        }
    },
    computed: {
        ...mapState([
            'userToken',
            'activeSite',
            'selectedAssignment'
        ]),
        disableOkButton () {
            return this.formDisabled || this.$v.form.$invalid
        },
        maxSizeInMB () {
            if (this.selectedAssignment) {
                return (this.selectedAssignment.maxSize / (1024 * 1024)).toFixed(0)
            }
            return ''
        },
        downloadCurrentFileURL () {
            if (this.currentFile) {
                return `${process.env.VUE_APP_API_URL}/sites/${this.activeSite.id}` +
                `/assignments/${this.selectedAssignment.id}/files/${this.currentFile.id}?token=${this.userToken}`
            }
            return '#'
        },
        displayCurrentFileSize () {
            if (this.currentFile) {
                if (this.currentFile.size > 1024 * 1024) {
                    return `${(this.currentFile.size / (1024 * 1024)).toFixed(2)} MB`
                } else {
                    return `${(this.currentFile.size / 1024).toFixed(2)} KB`
                }
            }
            return ''
        }
    },
    methods: {
        ...mapActions([
            'readStudentAssignmentFile',
            'createStudentAssignmentFile',
            'updateStudentAssignmentFile'
        ]),
        validateState (name) {
            const { $dirty, $error } = this.$v.form[name]
            return $dirty ? !$error : null
        },
        formatNames (files) {
            if (files.length === 1) {
                return files[0].name
            } else {
                return `${files.length} archivos seleccionados`
            }
        },
        async loadCurrentStudentAssignmentFile () {
            try {
                let payload = { courseSiteId: this.activeSite.id, assignmentId: this.selectedAssignment.id }
                this.currentFile = await this.readStudentAssignmentFile(payload)
                this.form.fileId = this.currentFile.id
            } catch (err) {
                console.log({ err: err })
                if (!err.response) {
                    // No internet error
                    this.$root.unknownErrorToast()
                } else if (err.response && (err.response.status !== 404)) {
                    // Any other error other than 404 case
                    this.$root.unknownErrorToast()
                }
                this.currentFile = null
            } finally {
                this.dataLoaded = true
            }
        },
        async onUpload (bvModalEvt) {
            bvModalEvt.preventDefault() // Prevent modal from closing
            await this.onSubmit() // Trigger submit handler
        },
        resetForm () {
            this.form.file = null
            this.form.fileId = null
            this.currentFile = null
            this.dataLoaded = false
            this.$v.$reset()
            this.$store.commit('RESET_SELECTED_ASSIGNMENT')
        },
        async onSubmit () {
            let loader = null
            try {
                loader = this.$root.$loading.show()
                this.formDisabled = true
                const payloadData = { ...this.form, courseSiteId: this.activeSite.id, assignmentId: this.selectedAssignment.id }
                if (this.currentFile) {
                    // Update mode
                    await this.updateStudentAssignmentFile(payloadData)
                    this.$root.infoToast(`Archivo de entrega editado.`)
                } else {
                    // Create mode
                    await this.createStudentAssignmentFile(payloadData)
                    this.$root.successToast(`Archivo entregado exitosamente.`)
                }
                this.$nextTick(() => {
                    this.$bvModal.hide('modal-student-upload-assignment')
                })
                this.resetForm()
            } catch (err) {
                console.log({ err: err })
                if (err.response && (err.response.status === 404 || err.response.status === 400)) {
                    this.$root.dangerToast('Datos incorrectos, vuelva a intentar.')
                } else {
                    this.$root.unknownErrorToast()
                }
            } finally {
                this.formDisabled = false
                loader.hide()
            }
        }
    }
}
</script>
